/** REZUMATUL DE BUSINESS CASE, desenat. Socoteala e a serverului
 *  (`cost_model.business_case`); rândurile vin scrise de `jetoane.dinServer`.
 *
 *  PE TABEL STAU NUMAI CIFRELE, ca în fișa de calcul: element, cantitate, UM,
 *  preț, valoare, % din TIC și observația scurtă. DE UNDE VINE fiecare se vede
 *  apăsând pe rând — acolo stau sursa, formula cu cifrele puse în ea, semnalul
 *  când două surse nu se potrivesc, și ecranul de unde se schimbă.
 *
 *  Cerut așa, în două rânduri: «the source should be in the drop down more info
 *  not on the main deviz» și «all of the information should be taken from what
 *  we have or documented with the source if we did not have it previously».
 *
 *  DEVIZUL PE ARTICOLE STĂ SUB RÂNDUL LUI. Săpătura și structura au, după
 *  calculul de detaliu, devizul de execuție în spate; desfăcând rândul, sub
 *  sursa lui se vede devizul acela, articol cu articol — «datele vechi să
 *  apară tot în formatul nou, dar când expandezi drop-down-ul corespunzător».
 *  Panoul nu știe să-l deseneze: îl primește gata în `detalii`, de la pagina
 *  care are devizul (`structure/Retete.tsx`).
 */
import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import { numar, useT } from "../../i18n";
import { bani, baniMilioane, simbolMoneda } from "../../structure/bani";
import HoverCard from "../../structure/HoverCard";
import { ani, eEstimat, type Detaliu, type Rand, type Rezumat } from "./businessCase";
import type { PunctBanda } from "./jetoane";
import Termeni, { Estimat, Note, Termen } from "./Termeni";

const cifre = (v: number, z = 2) =>
  numar(v, { minimumFractionDigits: z, maximumFractionDigits: z });
const procent = (v: number, z = 1) => `${cifre(v * 100, z)} %`;
/** O sumă în moneda ecranului, fără simbol — simbolul e în capul coloanei. */
const suma = (v: number) => bani(v, { zecimale: 2, simbol: false });

function Cifra({ r, tic }: { r: Rand; tic: number }) {
  const q = r.cantitate == null ? ""
    : r.procent ? procent(r.cantitate, r.zecimale ?? 0)
      : r.monetar ? bani(r.cantitate, { zecimale: r.zecimale ?? 0, simbol: false })
        : cifre(r.cantitate, r.zecimale ?? 0);
  // semnul stă pe cifra pe care se citește rândul: valoarea, când o are; un rând
  // care nu costă nimic (terenul plătit în natură) n-are ce estima
  const semn = eEstimat(r) && r.valoare !== 0 ? <Estimat deCe={r.observatie} /> : null;
  return (
    <>
      <td className="bc-n bc-cif">{r.valoare == null && q ? semn : null}{q}</td>
      <td className="bc-um">{r.um ?? ""}</td>
      <td className="bc-n bc-par">{r.parametru ?? ""}</td>
      <td className="bc-n bc-cif">{r.valoare == null ? "" : <>{semn}{suma(r.valoare)}</>}</td>
      <td className="bc-n bc-cif bc-um">
        {r.valoare == null || !tic ? "" : procent(r.valoare / tic)}
      </td>
      <td className="bc-obs"><Termeni text={r.observatie ?? ""} /></td>
    </>
  );
}

/** Panoul care se desface: tot ce face cifra justificată, plus devizul pe
 *  articole când rândul are unul. */
function DeUnde({ r, detaliu }: { r: Rand; detaliu?: ReactNode }) {
  const t = useT();
  return (
    <td colSpan={7} className="bc-det">
      <div className="bc-det-in">
        <div className="bc-det-sus">
          <span className={`bc-src bc-src-${r.sursa}`}>{t(`bc.sursa.${r.sursa}`)}</span>
          <span className="bc-um">{t("bc.det.sursa_cifrei")}</span>
        </div>
        <dl>
          <dt>{t("bc.det.de_unde")}</dt><dd><Termeni text={r.deUnde} /></dd>
          <dt>{t("bc.det.cum_iese")}</dt><dd><span className="bc-calc"><Termeni text={r.cumIese} /></span></dd>
        </dl>
        {r.semnal && (
          <div className="bc-semnal"><span aria-hidden="true">⚠</span>
            <span><Termeni text={r.semnal} /></span></div>
        )}
        {r.seSchimbaDin && (
          <p className="bc-drum">{t("bc.det.se_schimba_din")} <b>{r.seSchimbaDin}</b></p>
        )}
        {detaliu && <div className="bc-articole">{detaliu}</div>}
      </div>
    </td>
  );
}

export default function BusinessCase({ rez, banda, note = [], detalii = {}, scenariu, laScenarii }: {
  /** Rezumatul scenariului curent, scris în limba și moneda ecranului. */
  rez: Rezumat;
  /** Profitul net pe banda de prețuri din jurul celui ales: aceleași ipoteze,
   *  alt preț — cât de departe e pierderea, citit dintr-o privire. */
  banda: PunctBanda[];
  /** ce n-a putut aduna serverul (lista, programul, un deviz), spus omului */
  note?: string[];
  /** devizul pe articole care se desface sub rândul lui */
  detalii?: Partial<Record<Detaliu, ReactNode>>;
  /** Numele scenariului curent: un «și dacă» al asistentului mută pagina pe
   *  un scenariu nou, iar omul trebuie să vadă pe care stă. */
  scenariu?: string;
  laScenarii?: () => void;
}) {
  const t = useT();
  const [deschis, setDeschis] = useState<string | null>(null);
  const [cantitati, setCantitati] = useState(false);
  const maxBanda = Math.max(...banda.map((b) => Math.abs(b.net)), 1);

  const comuta = (cheie: string) => setDeschis(deschis === cheie ? null : cheie);

  const rand = (r: Rand, total = false) => {
    const detaliu = r.detaliu ? detalii[r.detaliu] : undefined;
    const clase = ["bc-rand", total && "bc-total", r.subtotal && "bc-subtotal",
      r.semnal && "bc-atentie", detaliu && "bc-cu-deviz"].filter(Boolean).join(" ");
    return (
      <Fragment key={r.cheie}>
        {r.grup && (
          <tr className="bc-grup"><td colSpan={7}>{r.grup}</td></tr>
        )}
        <tr className={clase} tabIndex={0}
            aria-expanded={deschis === r.cheie}
            onClick={() => comuta(r.cheie)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                comuta(r.cheie);
              }
            }}>
          <td>
            <span className="bc-sag">{deschis === r.cheie ? "▾" : "▸"}</span>
            <Termeni text={r.element} />
            {r.ajustare != null && (
              <span className="bc-eticheta-ajustare">
                {t("bc.ui.ajustat", { f: cifre(r.ajustare, 2) })}</span>
            )}
            {r.inLucru && (
              <HoverCard clasaAncora="bc-lucru" latime={240} eticheta={t("bc.det.in_lucru")}
                         continut={<span>{t("bc.det.in_lucru")}</span>}>{null}</HoverCard>
            )}
            {detaliu && <span className="bc-eticheta-deviz">{t("bc.det.pe_articole")}</span>}
          </td>
          <Cifra r={r} tic={rez.tic} />
        </tr>
        {deschis === r.cheie && (
          <tr className="bc-det-rand"><DeUnde r={r} detaliu={detaliu} /></tr>
        )}
      </Fragment>
    );
  };

  return (
    <div className="bc">
      <div className="bc-bara">
        {scenariu && (
          <>
            <span className="bc-lbl">{t("ip.scenariul")}</span>
            <b>{scenariu}</b>
            {laScenarii && (
              <button type="button" className="btn btng" onClick={laScenarii}>
                {t("ip.alt_scenariu")}</button>
            )}
          </>
        )}
        <Note note={note} />
      </div>

      <div className="bc-plci">
        <div className="bc-plc">
          <div className="bc-n2"><Termen termen="tic">{t("bc.ui.tic")}</Termen></div>
          <div className="bc-v">{baniMilioane(rez.tic)}</div>
          <div className="bc-d">{t("bc.ui.pe_m2_construit", { v: `${bani(rez.ticPeMp)}/m²` })}</div>
        </div>
        <div className="bc-plc">
          <div className="bc-n2"><Termen termen="cc">{t("bc.ui.constructii")}</Termen></div>
          <div className="bc-v">{baniMilioane(rez.constructii)}</div>
          <div className="bc-d">{t("bc.ui.din_tic", { p: procent(rez.tic ? rez.constructii / rez.tic : 0, 0) })}</div>
        </div>
        <div className="bc-plc">
          <div className="bc-n2">{t("bc.ui.venituri")}</div>
          <div className="bc-v">{baniMilioane(rez.venituri)}</div>
          <div className="bc-d">
            {t("bc.ui.din_proiect", { p: procent(1 - rez.ipoteze.cedatDinScd, 0) })}
          </div>
        </div>
        <div className={`bc-plc ${rez.profitNet >= 0 ? "bc-bun" : "bc-rau"}`}>
          <div className="bc-n2">{t("bc.ui.profit_net")}</div>
          <div className="bc-v">{baniMilioane(rez.profitNet)}</div>
          <div className="bc-d">
            {t("bc.ui.dupa_impozit", { p: procent(rez.ipoteze.cotaImpozit, 0) })}
          </div>
        </div>
        <div className={`bc-plc ${rez.roi >= 0 ? "bc-bun" : "bc-rau"}`}>
          <div className="bc-n2"><Termen termen="roi">ROI</Termen></div>
          <div className="bc-v">{procent(rez.roi)}</div>
          {/* ROI pe an = profit / capital-ani = ROI / (capital-ani / TIC): cu
              programul se arată anii medii de imobilizare, nu durata, ca cele
              două cifre să se lege (pe an iese mai mare când un euro stă < 1 an) */}
          <div className="bc-d">{rez.fluxDinProgram && rez.capitalAni != null && rez.tic > 0
            ? t("bc.ui.roi_pe_an_flux", {
              p: procent(rez.roiPeAn), m: ani(rez.capitalAni / rez.tic), n: ani(rez.ipoteze.duratiAni) })
            : t("bc.ui.roi_pe_an", { p: procent(rez.roiPeAn), n: ani(rez.ipoteze.duratiAni) })}</div>
        </div>
        {rez.capitalMax != null && (
          <div className="bc-plc">
            <div className="bc-n2"><Termen termen="capital_max">{t("bc.ui.capital_max")}</Termen></div>
            <div className="bc-v">{baniMilioane(rez.capitalMax)}</div>
            <div className="bc-d"><Termeni text={rez.rir != null ? t("bc.ui.capital_max_sub", { p: procent(rez.rir) }) : t("bc.ui.capital_max_fara")} /></div>
          </div>
        )}
        <div className="bc-plc">
          <div className="bc-n2"><Termen termen="echilibru">{t("bc.ui.echilibru")}</Termen></div>
          <div className="bc-v">{bani(rez.pragRentabilitate)}/m²</div>
          <div className="bc-d"><Termeni text={t("bc.r.prag.obs")} /></div>
        </div>
      </div>

      <section className="bc-banda" aria-labelledby="bc-banda-t">
        <div className="bc-banda-h"><b id="bc-banda-t">{t("bc.ui.banda")}</b>
          <span className="bc-um">{t("bc.ui.banda_sub")}</span></div>
        <div className="bc-banda-g">
          {banda.map((b) => (
            <Fragment key={b.pret}>
              <span className={`bc-par${b.baza ? " bc-banda-baza" : ""}`}>{bani(b.pret)}/m²</span>
              <span className="bc-banda-trk" aria-hidden="true">
                <span className="bc-banda-zero" />
                <i className={b.net < 0 ? "bc-banda-neg" : ""}
                   style={b.net < 0
                     ? { right: "70%", width: `${(-b.net / maxBanda) * 30}%` }
                     : { left: "30%", width: `${(b.net / maxBanda) * 70}%` }} />
              </span>
              <span className={`bc-cif${b.net < 0 ? " bc-banda-neg-t" : ""}`}>
                {baniMilioane(b.net)}</span>
            </Fragment>
          ))}
        </div>
      </section>

      <div className="bc-rama"><div className="bc-scroll">
        <table>
          <thead><tr>
            <th style={{ width: "28%" }}>{t("bc.th.element")}</th>
            <th className="bc-n">{t("bc.th.cantitate")}</th>
            <th><Termeni text={t("bc.th.um")} /></th>
            <th className="bc-n">{t("bc.th.parametru")}</th>
            <th className="bc-n">{t("bc.th.valoare", { moneda: simbolMoneda() })}</th>
            <th className="bc-n"><Termeni text={t("bc.th.din_tic")} /></th>
            <th style={{ width: "20%" }}>{t("bc.th.observatii")}</th>
          </tr></thead>
          <tbody>
            {/* IPOTEZELE își au ecranul lor; aici rămân cantitățile, strânse —
                se citesc o dată, nu la fiecare privire pe rezultat */}
            {rez.sectiuni.filter((s) => s.cheie !== "ipoteze").map((s) => (
              <Fragment key={s.cheie}>
                {s.cheie === "cantitati" ? (
                  <tr className="bc-cap bc-rand" tabIndex={0} aria-expanded={cantitati}
                      onClick={() => setCantitati(!cantitati)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setCantitati(!cantitati);
                        }
                      }}>
                    <td colSpan={7}><span className="bc-sag">{cantitati ? "▾" : "▸"}</span>
                      <strong>{t("bc.ui.ce_construim")}</strong></td>
                  </tr>
                ) : (
                  <tr className="bc-cap"><td colSpan={7}><strong><Termeni text={s.titlu} /></strong></td></tr>
                )}
                {(s.cheie !== "cantitati" || cantitati) && s.randuri.map((r) => rand(r))}
                {s.total && rand(s.total, true)}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}
